import { type MutableRefObject, useLayoutEffect } from 'react';

interface UseResizeObserverProps<T> {
  target: MutableRefObject<T | null>;
  onResize: (entry: T) => void;
}

/**
 * Register a `target` ref to a `ResizeObserver` instance and call an
 * `onResize` callback whenever the target element experiences a `resize` event.
 */
export const useResizeObserver = <T extends HTMLElement>({
  target,
  onResize,
}: UseResizeObserverProps<T>) => {
  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onResize(entry.target as T);
      }
    });

    if (target.current) {
      resizeObserver.observe(target.current);
      onResize(target.current);
    }

    return () => resizeObserver.disconnect();
  }, [onResize, target]);
};
